import { DateTime } from "luxon"
import { useField, useFormikContext } from "formik"
import { useCallback, useEffect } from "react"
import { Stack } from "@mui/material"
import { TimePicker } from "@mui/x-date-pickers"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { DeliveryTimesField, DynamicConfigField, DynamicConfigKey, MeetingServiceTabValues } from "../../form/formTypes"
import { NullableCalendarEvent } from "../../resource/calendar/fullCalendarTypes"
import {
    createInitEvents,
    DEFAULT_DURATION_MINUTES,
    DEFAULT_MINIMUM_DURATION_MINUTES,
} from "../../resource/resourceBookingCalendars/helpers"
import { getFormikFieldError } from "../../form/helpers"
import { roundToNearest5Minutes } from "../../helpers"

type DeferredMeetingTimeFieldsProps = Readonly<{
    meetingTimeFields: {
        startTime?: DynamicConfigField
        endTime?: DynamicConfigField
    }
}>

export function DeferredMeetingTimeFields({ meetingTimeFields }: DeferredMeetingTimeFieldsProps) {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const now = dateTimeFactory.now()

    const { submitCount } = useFormikContext<MeetingServiceTabValues>()

    const [{ value: currentStart }, startMeta, startHelpers] = useField<DateTime | null>(
        DynamicConfigKey.MEETING_START_TIME,
    )
    const [{ value: currentEnd }, endMeta, endHelpers] = useField<DateTime | null>(DynamicConfigKey.MEETING_END_TIME)
    const [{ value: event }, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [{ value: multipleDeliveries }] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const eventStart = event?.start ? dateTimeFactory.fromISO(event.start) : null

    const deliveryDate = multipleDeliveries?.deliveryDate
    const deliveryTime = multipleDeliveries?.deliveryTimes?.[0]?.time

    const deliveryDateTime =
        deliveryDate && deliveryTime ? dateTimeFactory.fromISO(`${deliveryDate}T${deliveryTime}`) : null

    const hasCurrentStart = !!currentStart

    useEffect(() => {
        if (!meetingTimeFields.startTime || currentStart) return

        const initFromDelivery = deliveryDateTime && deliveryDateTime > now

        if (eventStart) {
            // The timeline event owns its own time (a booking may start at any minute, e.g. right on the
            // now-indicator), so it is taken as is — re-rounding would show a start the booking doesn't have.
            startHelpers.setValue(eventStart)
        } else if (initFromDelivery) {
            startHelpers.setValue(roundToNearest5Minutes(deliveryDateTime))
        }
    }, [deliveryDateTime?.toISO(), eventStart?.toISO(), hasCurrentStart])

    const { startTime, endTime } = meetingTimeFields
    if (!startTime && !endTime) return null

    const startLabel = l10n.getString("create-order-extra-services-additional-fields-start-time-label") + " *"
    const endLabel = l10n.getString("create-order-extra-services-additional-fields-end-time-label") + " *"

    const startErrorProps = getFormikFieldError(startMeta, submitCount)
    const endErrorProps = getFormikFieldError(endMeta, submitCount)

    const minEndTime = currentStart
        ? currentStart.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES })
        : now.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES })

    const handleStartChange = useCallback(
        (value: DateTime | null) => {
            if (!value) {
                startHelpers.setValue(null)
                return
            }
            const roundedStart = roundToNearest5Minutes(value)
            startHelpers.setValue(roundedStart)

            const roundedStartValue = dateTimeFactory.toApiISOMinutesFormat(roundedStart)

            if (!currentEnd || roundedStart > currentEnd) {
                const roundedEnd = roundedStart.plus({ minutes: DEFAULT_DURATION_MINUTES })
                endHelpers.setValue(roundedEnd)
                eventHelpers.setValue(
                    createInitEvents([], roundedStartValue, dateTimeFactory.toApiISOMinutesFormat(roundedEnd)),
                )
            } else {
                eventHelpers.setValue(
                    createInitEvents([], roundedStartValue, dateTimeFactory.toApiISOMinutesFormat(currentEnd)),
                )
            }
        },
        [currentEnd, dateTimeFactory, startHelpers, endHelpers, eventHelpers],
    )

    const handleEndChange = useCallback(
        (value: DateTime | null) => {
            if (!value) {
                endHelpers.setValue(null)
                return
            }
            const roundedEnd = roundToNearest5Minutes(value)
            endHelpers.setValue(roundedEnd)

            if (!currentStart) return
            eventHelpers.setValue(
                createInitEvents(
                    [],
                    dateTimeFactory.toApiISOMinutesFormat(currentStart),
                    dateTimeFactory.toApiISOMinutesFormat(roundedEnd),
                ),
            )
        },
        [currentStart, dateTimeFactory, endHelpers, eventHelpers],
    )

    return (
        <Stack direction="row" gap="1rem">
            {startTime && (
                <TimePicker
                    value={currentStart}
                    label={startLabel}
                    ampm={false}
                    minutesStep={5}
                    onChange={handleStartChange}
                    slotProps={{ textField: { fullWidth: true, size: "small", ...startErrorProps } }}
                />
            )}
            {endTime && (
                <TimePicker
                    value={currentEnd}
                    label={endLabel}
                    ampm={false}
                    minutesStep={5}
                    minTime={minEndTime}
                    onChange={handleEndChange}
                    slotProps={{ textField: { fullWidth: true, size: "small", ...endErrorProps } }}
                />
            )}
        </Stack>
    )
}
